import { useEffect, useState, useRef } from "react";
import proj4 from "proj4";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import axios from "axios";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";


// تعریف سیستم مختصات
proj4.defs("EPSG:26917", "+proj=utm +zone=17 +datum=NAD83 +units=m +no_defs");
const sourceProj = "EPSG:26917";
const destProj = "EPSG:4326";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_BASE = process.env.REACT_APP_API_BASE || "";

const PropertiesList = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 🔹 asc یا desc

  const mapRef = useRef(null);
  const fetchTimeout = useRef(null);

  // 🔹 Fetch فقط مختصات داخل bounds فعلی
 const fetchPropertiesInBounds = async (map, sortOrder = null) => {
  if (!map) return;
  const bounds = map.getBounds();
  if (!bounds) return;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  try {
    const res = await axios.get(`${API_BASE}/api/properties/`, {
      params: {
        ne_lat: ne.lat(),
        ne_lng: ne.lng(),
        sw_lat: sw.lat(),
        sw_lng: sw.lng(),
        ordering: sortOrder === "asc" ? "score" : sortOrder === "desc" ? "-score" : null,
      },
    });
    setLocations(res.data.results || res.data);
  } catch (err) {
    console.error("Failed to fetch properties in bounds:", err);
  }
};


  // 🔹 Load Google Maps script
  const loadGoogleMapsScript = (callback) => {
    if (window.google && window.google.maps) return callback();
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", callback);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}`;
    script.id = "google-maps-script";
    script.async = true;
    script.defer = true;
    script.onload = callback;
    script.onerror = () => console.error("❌ Failed to load Google Maps script.");
    document.body.appendChild(script);
  };

  // 🔹 Initialize Map
  useEffect(() => {
    loadGoogleMapsScript(() => {
      const map = new window.google.maps.Map(document.getElementById("map"), {
        zoom: 12,
        center: { lat: 43.6532, lng: -79.3832 }, // مرکز اولیه Toronto
      });

      // fetch اولیه
      fetchPropertiesInBounds(map);

      // listener برای تغییر bounds
      map.addListener("bounds_changed", () => {
        if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
        fetchTimeout.current = setTimeout(() => fetchPropertiesInBounds(map), 300);
      });

      mapRef.current = map;
    });
  }, []);


// 🔹 Sort handler
const handleSortByScore = () => {
  let newOrder = sortOrder === "asc" ? "desc" : "asc";
  setSortOrder(newOrder);

  if (mapRef.current) {
    fetchPropertiesInBounds(mapRef.current, newOrder);
  }
};




  // 🔹 Update markers وقتی locations تغییر کنند
  useEffect(() => {
    if (!window.google || !window.google.maps || !mapRef.current) return;

    const map = mapRef.current;
    const infoWindows = [];

    // پاک کردن markers قبلی
    if (window.markerCluster) window.markerCluster.clearMarkers();

    const markers = locations.map((loc) => {
      if (!loc.lat || !loc.lng) return null;

      const marker = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map,
        title: loc.address ?? "Unknown address",
      });

      const contentString = `
        <div style="max-width:250px;">
<strong>📍 ${loc.address ?? "Unknown address"}</strong><br/> 
🏗 Structure: ${loc.structure_type ?? "N/A"}<br/> 
🔖 Permit: ${loc.permit_number ?? "N/A"}<br/> 
📝 Status: ${loc.status ?? "N/A"}<br/> 
🏢 Use: ${loc.current_use ?? "N/A"} → ${loc.proposed_use ?? "N/A"}<br/> 
🧱 Builder: ${loc.builder_name ?? "N/A"}<br/> 

🏙 Nearest Regional Corridor: ${loc.distanceToCorridorKm != null ? loc.distanceToCorridorKm.toFixed(1) : "N/A"} km<br/> 
🚌 Nearest Bus Stop: ${loc.nearestBusStopName ?? "N/A"} (${loc.distanceToBusStopKm != null ? loc.distanceToBusStopKm.toFixed(1) : "N/A"} km)<br/> 
🚏 Nearest Bus Terminal: ${loc.nearestBusTerminalName ?? "N/A"} (${loc.distanceToBusTerminalKm != null ? loc.distanceToBusTerminalKm.toFixed(1) : "N/A"} km)<br/> 
🚉 Nearest GO Station: ${loc.nearestGOStationName ?? "N/A"} (${loc.distanceToGOStationKm != null ? loc.distanceToGOStationKm.toFixed(1) : "N/A"} km)<br/> 
🚇 Nearest Subway Station: ${loc.nearestSubwayStationName ?? "N/A"} (${loc.distanceToSubwayStationKm != null ? loc.distanceToSubwayStationKm.toFixed(1) : "N/A"} km)<br/> 
🚋 Nearest Surface Rapid Transit: ${loc.nearestSurfaceRapidTransitName ?? "N/A"} (${loc.distanceToSurfaceRapidTransitKm != null ? loc.distanceToSurfaceRapidTransitKm.toFixed(1) : "N/A"} km)<br/> 
🟢 Distance to Green Land: ${loc.distanceToGreenLandKm != null ? loc.distanceToGreenLandKm.toFixed(1) : "N/A"} km<br/> 
🌆 Nearest Urban Growth Area: ${loc.nearestUrbanGrowthName ?? "N/A"} (${loc.distanceToUrbanGrowthKm != null ? loc.distanceToUrbanGrowthKm.toFixed(1) : "N/A"} km)<br/> 
🥾 Nearest Trail: ${loc.nearestTrailName ?? "N/A"} (${loc.distanceToTrailKm != null ? loc.distanceToTrailKm.toFixed(1) : "N/A"} km)<br/> 
👶 Nearest Children Service: ${loc.nearestChildrenServiceName ?? "N/A"} (${loc.distanceToChildrenServiceKm != null ? loc.distanceToChildrenServiceKm.toFixed(1) : "N/A"} km)<br/> 
🏥 Nearest Hospital: ${loc.nearestHospitalName ?? "N/A"} (${loc.distanceToHospitalKm != null ? loc.distanceToHospitalKm.toFixed(1) : "N/A"} km)<br/> 
🏛 Nearest City Facility: ${loc.nearestCityFacilityName ?? "N/A"} (${loc.distanceToCityFacilityKm != null ? loc.distanceToCityFacilityKm.toFixed(1) : "N/A"} km)<br/> 

⭐ Score: <strong>${typeof loc.score === "number" ? loc.score.toFixed(2) : "N/A"} / 10</strong>

        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({ content: contentString });
      marker.addListener("click", () => {
        infoWindows.forEach((iw) => iw.close());
        infoWindow.open(map, marker);
        setSelectedLocation(loc);
      });
      infoWindows.push(infoWindow);
      return marker;
    });

    window.markerCluster = new MarkerClusterer({ map, markers: markers.filter((m) => m !== null) });
  }, [locations]);

  return (
    <div>
      <header className="container_header">
        <img className="logo" src={logo} alt="Logo" />
        <div className="links">
          <div className="sub_links"><Link to="/">Home</Link></div>
          <div className="sub_links"><Link to="/">Services</Link></div>
          <div className="sub_links"><Link to="/">Articles</Link></div>
          <div className="sub_links"><Link to="/">News</Link></div>
          <div className="sub_links"><Link to="/">Events</Link></div>
        </div>
        <button className="contact_link">Contact Us</button>
      </header>

      <div id="map" style={{ height: "600px", width: "100%", marginTop: "20px", border: "1px solid #ccc" }} />

      <table className="min-w-full border border-gray-200 shadow-sm rounded-lg overflow-hidden mt-6">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Address</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Structure Type</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Permit</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Use</th>
            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Builder</th>
            <th
              className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer select-none"
              onClick={handleSortByScore}
            >
              <div className="flex items-center gap-1" style={{
                cursor:"pointer"
              }}>
                Score
                {sortOrder === null && <ArrowUpDown size={16} className="text-gray-400" />}
                {sortOrder === "asc" && <ArrowUp size={16} className="text-blue-600" />}
                {sortOrder === "desc" && <ArrowDown size={16} className="text-blue-600" />}
              </div>
            </th>       </tr>
        </thead>
        <tbody>
          {locations.map((loc, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className="px-6 py-3">
                <a href={`/projects/${loc.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline">
                  {`${loc.street_num} - ${loc.street_name}`}
                </a>
              </td>              <td className="px-6 py-3">{loc.structure_type}</td>
              <td className="px-6 py-3">{loc.work}</td>
              <td className="px-6 py-3">{loc.status}</td>
              <td className="px-6 py-3">{loc.current_use} → {loc.proposed_use}</td>
              <td className="px-6 py-3">{loc.builder_name}</td>
              <td className="px-6 py-3">{typeof loc.score === "number" ? loc.score.toFixed(2) : "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PropertiesList;
